// What Home shows, as rules: Today in GTD terms (gtdToday), the containers
// an item lives in and which are hidden (containerOf, withoutArchived), and
// GTD's stuck projects (stuckProjects). Web and mobile use them through
// today.ts and para.ts; generate-digest imports this file directly, so the
// scheduled digest is made from the same rules as Home. It imports nothing
// but types, each by its full path, so Deno can load it as it is.

import type { ContainerKind, ContainerRow, Project, Task } from './types/index.ts';

export type TodayTask = Pick<Task, 'id' | 'status' | 'due_date' | 'focus_date'>;

export interface TodayBuckets<T extends TodayTask> {
  /** Selected for today's focus. */
  focus: T[];
  /** Due before today, not selected. */
  overdue: T[];
  /** Due today, not selected. */
  dueToday: T[];
  /** Everything else still open: future due dates first (soonest first), then undated. */
  open: T[];
}

/** Split open tasks into the daily view's groups. Done tasks are excluded. */
export function bucketTasksForToday<T extends TodayTask>(tasks: T[], today: string): TodayBuckets<T> {
  const buckets: TodayBuckets<T> = { focus: [], overdue: [], dueToday: [], open: [] };
  for (const task of tasks) {
    if (task.status === 'done') continue;
    if (task.focus_date === today) buckets.focus.push(task);
    else if (task.due_date && task.due_date < today) buckets.overdue.push(task);
    else if (task.due_date === today) buckets.dueToday.push(task);
    else buckets.open.push(task);
  }
  buckets.overdue.sort((a, b) => (a.due_date as string).localeCompare(b.due_date as string));
  buckets.open.sort((a, b) => {
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return 0;
  });
  return buckets;
}

/** A task as the GTD Today view reads it. */
export type GtdTodayTask = TodayTask & Pick<Task, 'gtd_status' | 'context'>;

export interface ContextGroup<T> {
  /** The context these next actions share; null for next actions with none. */
  context: string | null;
  tasks: T[];
}

export interface GtdToday<T extends GtdTodayTask> {
  /** Selected for today's focus. */
  focus: T[];
  /** Overdue (oldest first), then due today; not in focus. */
  due: T[];
  /**
   * Focus tasks that are overdue or due today, oldest due date first. They
   * are listed in Focus only; Due counts and names them, so Due never reads as
   * nothing due while a focused task is due.
   */
  dueInFocus: T[];
  /** Every other open next action, grouped by context. */
  next: ContextGroup<T>[];
  /** Waiting on someone, not in focus or due. */
  waiting: T[];
  /** Open someday tasks. Today does not show them; the weekly review does. */
  somedayCount: number;
}

/**
 * Today in GTD terms: Focus, then Due (the hard landscape), then the next
 * actions grouped by context, then what is waiting on someone. Someday tasks
 * are left out (only counted), and done tasks are excluded. Context groups
 * follow the user's list, then any other context alphabetically, then next
 * actions with no context; within a group, dated tasks come first.
 */
export function gtdToday<T extends GtdTodayTask>(tasks: T[], today: string, contexts: readonly string[]): GtdToday<T> {
  const somedayCount = tasks.filter(t => t.gtd_status === 'someday' && t.status !== 'done').length;
  const buckets = bucketTasksForToday(tasks.filter(t => t.gtd_status !== 'someday'), today);
  const groups = new Map<string | null, T[]>();
  const waiting: T[] = [];
  for (const task of buckets.open) {
    if (task.gtd_status === 'waiting') {
      waiting.push(task);
      continue;
    }
    groups.set(task.context, [...(groups.get(task.context) ?? []), task]);
  }
  const rank = (context: string | null): [number, number, string] => {
    if (context === null) return [2, 0, ''];
    const position = contexts.indexOf(context);
    return position >= 0 ? [0, position, ''] : [1, 0, context];
  };
  const order = [...groups.keys()].sort((a, b) => {
    const [ra, rb] = [rank(a), rank(b)];
    return ra[0] - rb[0] || ra[1] - rb[1] || ra[2].localeCompare(rb[2]);
  });
  return {
    focus: buckets.focus,
    due: [...buckets.overdue, ...buckets.dueToday],
    dueInFocus: buckets.focus
      .filter(t => t.due_date !== null && t.due_date <= today)
      .sort((a, b) => (a.due_date as string).localeCompare(b.due_date as string)),
    next: order.map(context => ({ context, tasks: groups.get(context) as T[] })),
    waiting,
    somedayCount,
  };
}

export interface ContainerRef {
  kind: ContainerKind;
  id: string;
}

/**
 * The container a task or note lives in, or null when it has none (a row
 * fetched before it was filed). More than one container is a broken row,
 * reported rather than guessed at.
 */
export function containerOf(item: {
  project_id?: string | null;
  area_id?: string | null;
  resource_id?: string | null;
}): ContainerRef | null {
  const set: ContainerRef[] = [];
  if (item.project_id) set.push({ kind: 'project', id: item.project_id });
  if (item.area_id) set.push({ kind: 'area', id: item.area_id });
  if (item.resource_id) set.push({ kind: 'resource', id: item.resource_id });
  if (set.length > 1) {
    throw new Error(`An item lives in more than one container: ${set.map(c => c.kind).join(', ')}`);
  }
  return set[0] ?? null;
}

/**
 * Items whose container is not archived. An item with no container, or whose
 * container is not in the map (a list fetched before the container was
 * created), stays: hiding work is worse than showing it.
 */
export function withoutArchived<T extends { project_id?: string | null; area_id?: string | null; resource_id?: string | null }>(
  items: readonly T[],
  containersById: ReadonlyMap<string, Pick<ContainerRow, 'archived_at'>>,
): T[] {
  return items.filter(item => {
    const container = containerOf(item);
    return !container || !containersById.get(container.id)?.archived_at;
  });
}

/**
 * Active projects with no open next action: GTD's "stuck" projects. A
 * waiting, blocked, someday, done or archived project is not stuck.
 */
export function stuckProjects(
  projects: readonly Pick<Project, 'id' | 'status' | 'archived_at'>[],
  tasks: readonly Pick<Task, 'project_id' | 'status' | 'gtd_status'>[],
): Set<string> {
  const moving = new Set(tasks.filter(t => t.project_id && t.status !== 'done' && t.gtd_status === 'next').map(t => t.project_id));
  return new Set(projects.filter(p => p.status === 'active' && !p.archived_at && !moving.has(p.id)).map(p => p.id));
}
